import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { trigger, transition, style, animate } from '@angular/animations';
import { BadgeConquistadaResponse } from 'app/shared/models/ei-models';

@Component({
  selector: 'app-badge-toast',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <div class="badge-toast" [@slideIn]>
      <div class="toast-badge-icon">
        <img 
          *ngIf="data.badgeImagemUrl" 
          [src]="data.badgeImagemUrl" 
          [alt]="data.badgeNome"
          class="badge-image"
        />
        <div *ngIf="!data.badgeImagemUrl" class="badge-placeholder">
          <mat-icon>military_tech</mat-icon>
        </div>
        <div class="badge-glow"></div>
      </div>
      
      <div class="toast-content">
        <div class="toast-header">
          <span class="badge-label">🏅 Nova Badge!</span>
        </div>
        <h4 class="badge-nome">{{ data.badgeNome }}</h4>
        <p class="badge-descricao">{{ data.badgeDescricao }}</p>
        <div class="toast-footer" *ngIf="data.pontos">
          <span class="bonus">
            <mat-icon>add_circle</mat-icon>
            +{{ data.pontos }} pontos bônus
          </span>
        </div>
      </div>
      
      <button class="toast-close" (click)="fechar()">
        <mat-icon>close</mat-icon>
      </button>
      
      <div class="toast-progress">
        <div class="progress-bar" [style.animation-duration.ms]="duracaoMs"></div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
    
    .badge-toast {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
      border: 1px solid rgba(147, 51, 234, 0.3);
      border-radius: 12px;
      padding: 16px;
      min-width: 340px;
      max-width: 420px;
      position: relative;
      overflow: hidden;
      box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.4),
        0 0 20px rgba(147, 51, 234, 0.15);
    }
    
    .toast-badge-icon {
      position: relative;
      flex-shrink: 0;
    }
    
    .badge-image {
      width: 56px;
      height: 56px;
      border-radius: 8px;
      object-fit: contain;
      background: rgba(255, 255, 255, 0.05);
      padding: 4px;
      animation: badge-shine 2s ease-in-out infinite;
    }
    
    .badge-placeholder {
      width: 56px;
      height: 56px;
      border-radius: 8px;
      background: linear-gradient(135deg, #9333ea, #c084fc);
      display: flex;
      align-items: center;
      justify-content: center;
      
      mat-icon {
        color: white;
        font-size: 32px;
        width: 32px;
        height: 32px;
      }
    }
    
    .badge-glow {
      position: absolute;
      inset: -4px;
      border-radius: 12px;
      background: linear-gradient(135deg, rgba(147, 51, 234, 0.4), rgba(192, 132, 252, 0.2));
      filter: blur(8px);
      z-index: -1;
      animation: glow-pulse 2s ease-in-out infinite;
    }
    
    @keyframes badge-shine {
      0%, 100% { filter: brightness(1); }
      50% { filter: brightness(1.2); }
    }
    
    @keyframes glow-pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }
    
    .toast-content {
      flex: 1;
      min-width: 0;
    }
    
    .toast-header {
      margin-bottom: 4px;
    }
    
    .badge-label {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #c084fc;
    }
    
    .badge-nome {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 700;
      color: white;
    }
    
    .badge-descricao {
      margin: 0 0 8px 0;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      line-height: 1.4;
    }
    
    .toast-footer {
      font-size: 12px;
      
      .bonus {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #4ade80;
        font-weight: 500;
        
        mat-icon {
          font-size: 14px;
          width: 14px;
          height: 14px;
        }
      }
    }
    
    .toast-close {
      position: absolute;
      top: 8px;
      right: 8px;
      background: transparent;
      border: none;
      padding: 4px;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.4);
      border-radius: 4px;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.1);
      }
      
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }
    
    .toast-progress {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: rgba(255, 255, 255, 0.1);
      overflow: hidden;
    }
    
    .progress-bar {
      height: 100%;
      background: linear-gradient(90deg, #9333ea, #c084fc);
      animation: progress linear forwards;
      transform-origin: left;
    }
    
    @keyframes progress {
      from { transform: scaleX(1); }
      to { transform: scaleX(0); }
    }
  `],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateX(100%)' }))
      ])
    ])
  ]
})
export class BadgeToastComponent implements OnInit {
  duracaoMs = 10000; // 10 segundos
  
  constructor(
    public snackBarRef: MatSnackBarRef<BadgeToastComponent>,
    @Inject(MAT_SNACK_BAR_DATA) public data: BadgeConquistadaResponse
  ) {}
  
  ngOnInit(): void {}
  
  fechar(): void {
    this.snackBarRef.dismiss();
  }
}
